import { Avatar, Box, Button, Card, CardContent, Chip, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import { ReactElement, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ToastOptions, toast } from 'react-toastify';
import QrScan from '../../component/qrScan/QrScan';
import useAppDispatch from '../../hooks/useAppDispatch';
import { getVehicleManifest, possessItemByIdentifier } from '../../redux/slices/assignments/assignmentsSlice';

const toastOptions = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'dark',
} as ToastOptions;

type RecoveredItem = {
  itemId: string;
  identifier: string;
  productName: string;
  imageSrc?: string;
};

const possessErrorText = (rejection: unknown): string => {
  const status = (rejection as { status?: number })?.status;
  const messages = (rejection as { messages?: string[] })?.messages;
  const joined = Array.isArray(messages) ? messages.join(' ') : '';
  if (status === 409 && joined.includes('Already in your possession')) {
    return 'Already on your truck.';
  }
  if (status === 409 && joined.includes('yard')) {
    return 'Item is at the yard — use Pickup for its assignment.';
  }
  if (status === 409) {
    return 'Item is not at a job site.';
  }
  if (status === 404) {
    return 'Item not found.';
  }
  return 'Unable to recover the scanned item. Please try again.';
};

// Batch scan-to-recover: the scanner reopens after every scan so a tech can
// work through a pile of equipment; Done finishes the session.
export const Recover = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showQr, setShowQr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recovered, setRecovered] = useState<RecoveredItem[]>([]);
  // Set when a scan was handled; the scanner's onClose then reopens it so the
  // batch continues. A manual close (no scan) really closes.
  const scannedRef = useRef(false);

  const checkScanResult = async (decodedText: string) => {
    scannedRef.current = true;
    const identifier = decodedText.replace(/^0[xX]/, '');
    setBusy(true);
    try {
      const result = await dispatch(possessItemByIdentifier({ identifier, options: { hideError: true } })).unwrap();
      const item = result.data.result;
      if (item?.item_id && !recovered.some((entry) => entry.itemId === item.item_id)) {
        setRecovered((list) => [
          { itemId: item.item_id ?? '', identifier: item.identifier ?? identifier, productName: item.product_name ?? '', imageSrc: item.image_src },
          ...list,
        ]);
      }
    } catch (rejection) {
      toast.error(possessErrorText(rejection), toastOptions);
    } finally {
      setBusy(false);
    }
  };

  const handleScannerClose = () => {
    setShowQr(false);
    if (scannedRef.current) {
      scannedRef.current = false;
      // Reopen for the next item in the batch.
      window.setTimeout(() => setShowQr(true), 400);
    }
  };

  const handleDone = () => {
    dispatch(getVehicleManifest({ hideError: true }));
    navigate('/field-service');
  };

  return (
    <>
      <Helmet title="Recover" />
      <Stack spacing={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h4">Recover</Typography>
          <Chip label={`${recovered.length} recovered`} color={recovered.length > 0 ? 'success' : 'default'} />
        </Box>

        <Typography color="text.secondary">
          Scan each item you are taking back. The scanner reopens after every scan — close it or tap Done when the truck is loaded.
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={() => setShowQr(true)}
          disabled={busy}
          startIcon={<Box component="i" className="tags-icon-barcode" />}
          sx={{ minHeight: 48 }}
        >
          {recovered.length > 0 ? 'Keep Scanning' : 'Start Scanning'}
        </Button>

        {recovered.length > 0 && (
          <Card>
            <List dense>
              {recovered.map((item) => (
                <ListItem key={item.itemId}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={item.imageSrc || undefined}>
                      <Box component="i" className="tags-icon-barcode" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={item.productName} secondary={item.identifier} />
                </ListItem>
              ))}
            </List>
          </Card>
        )}

        {recovered.length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Recovered items appear here as you scan them.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Button fullWidth variant="outlined" size="large" sx={{ minHeight: 48 }} onClick={handleDone}>
          Done
        </Button>
      </Stack>

      <QrScan open={showQr} onClose={handleScannerClose} checkScanResult={checkScanResult} />
    </>
  );
};

export default Recover;
